import React, { useEffect, useState, useCallback } from "react";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { useNavigate, Link, useParams } from "react-router-dom";
import config from "../config";
import GameCreationForm from "./GameCreationForm";
import GameDisplay from "./GameDisplay";
import axios from "axios";
import "../styles/Home.css";

// Helper function to get API base URL (copied from GamePage)
const getApiBaseUrl = () =>
  config.apiUrl || process.env.REACT_APP_API_URL || "http://localhost:3000";

function Home() {
  const navigate = useNavigate();
  const { pagePath } = useParams(); // For when accessed via /pages/:pagePath
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [gameExists, setGameExists] = useState(false);
  const [playersList, setPlayersList] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [playersError, setPlayersError] = useState(null);
  const [fullGameData, setFullGameData] = useState(null);

  // Function to fetch players (only called by owner if game exists)
  const fetchPlayers = useCallback(async (currentGameId) => {
    if (!currentGameId || !auth.currentUser) return;

    console.log(`Home: Fetching players for game ${currentGameId}`);
    setPlayersLoading(true);
    setPlayersError(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.get(
        `${getApiBaseUrl()}/api/games/${currentGameId}/players`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPlayersList(response.data.players || []);
      console.log("Home: Players fetched:", response.data.players);
    } catch (err) {
      console.error("Home: Error fetching players list:", err);
      setPlayersError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load players list."
      );
      setPlayersList([]);
    } finally {
      setPlayersLoading(false);
    }
  }, []); // Empty dependency array as it uses auth.currentUser directly

  // Function to fetch full game data (including state)
  const fetchFullGameData = useCallback(async (currentGameId) => {
    if (!currentGameId || !auth.currentUser) return null;
    console.log(`Home: Fetching full game data for game ${currentGameId}`);
    try {
      const token = await auth.currentUser.getIdToken();
      // Use the /api/games/:gameId endpoint which now returns state
      const response = await axios.get(
        `${getApiBaseUrl()}/api/games/${currentGameId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.gameExists) {
        console.log("Home: Full game data fetched:", response.data.game);
        return response.data.game; // Return the game data
      } else {
        console.warn(
          "Home: fetchFullGameData did not find game or received unexpected structure."
        );
        return null;
      }
    } catch (error) {
      console.error("Home: Error fetching full game data:", error);
      setError("Failed to fetch latest game details."); // Update main error state
      return null;
    }
    // No loading state managed here, handled by callers
  }, []); // Dependency: getApiBaseUrl (implicitly)

  useEffect(() => {
    const fetchPageAndGameData = async () => {
      setLoading(true);
      setError(null);
      setPlayersError(null);
      setFullGameData(null); // Reset full game data
      if (!pagePath) {
        // If there's no pagePath in the URL, try to get the user's active page
        try {
          const token = await auth.currentUser?.getIdToken();
          if (!token) {
            navigate("/"); // Redirect to login if not authenticated
            return;
          }
          const response = await fetch(`${config.apiUrl}/api/pages/my-page`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            const data = await response.json();
            // Redirect to the user's page path
            navigate(`/pages/${data.pagePath}`, { replace: true });
            // The component will re-render with the new pagePath, triggering the fetch below
          } else {
            setError("Could not find or create your page.");
            setLoading(false);
          }
        } catch (err) {
          console.error("Error fetching user page:", err);
          setError("Error fetching page data.");
          setLoading(false);
        }
        return; // Exit effect until redirect happens
      }

      // --- We have a pagePath, now fetch game data for this page ---
      try {
        const token = await auth.currentUser?.getIdToken(true); // Force refresh token
        if (!token) {
          navigate("/"); // Should not happen if we got here, but safety check
          return;
        }

        const gameResponse = await fetch(
          `${getApiBaseUrl()}/api/games/by-page/${pagePath}`,
          {
            headers: {
              Authorization: `Bearer ${token}`, // Send token to identify user
            },
          }
        );

        if (!gameResponse.ok) {
          // Handle specific errors like 404 (page not found) vs 500
          const errorData = await gameResponse.json().catch(() => ({})); // Try to get error details
          throw new Error(
            errorData.error || `HTTP error! status: ${gameResponse.status}`
          );
        }

        const result = await gameResponse.json();
        const ownerStatus = result.isOwner;
        const gameExistsStatus = result.gameExists;

        setIsOwner(ownerStatus);
        setGameExists(gameExistsStatus);

        // --- If owner and game exists, fetch FULL data and players ---
        if (ownerStatus && gameExistsStatus && result.game?.id) {
          // Fetch full data initially
          const initialFullData = await fetchFullGameData(result.game.id);
          setFullGameData(initialFullData);
          // Fetch players
          fetchPlayers(result.game.id);
        } else if (!ownerStatus && gameExistsStatus && result.game?.id) {
          // Non-owner, fetch full data just for display (no roles needed)
          const initialFullData = await fetchFullGameData(result.game.id);
          setFullGameData(initialFullData); // For GameDisplay
        }
        // ---
      } catch (err) {
        console.error("Error fetching game data:", err);
        setError(`Failed to load game data: ${err.message}`);
        // Decide how to handle errors - maybe show a generic error, or specific messages
      } finally {
        setLoading(false);
      }
    };

    // Ensure user is authenticated before fetching
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchPageAndGameData();
      } else {
        navigate("/"); // Redirect to login if user logs out while on this page
      }
    });

    return () => unsubscribe(); // Cleanup subscription

    // Rerun effect if pagePath changes
  }, [navigate, pagePath, fetchPlayers, fetchFullGameData]);

  // --- Effect for Polling Player List (Owner Only) ---
  useEffect(() => {
    let intervalId = null;

    if (isOwner && gameExists && fullGameData?.id) {
      console.log(
        `Home: Starting player list polling for game ${fullGameData.id}`
      );
      // Call immediately first time
      fetchPlayers(fullGameData.id);

      intervalId = setInterval(() => {
        console.log(`Home: Polling player list for game ${fullGameData.id}`);
        fetchPlayers(fullGameData.id);
      }, 5000); // Poll every 5 seconds
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        console.log(
          `Home: Stopping player list polling for game ${fullGameData?.id}`
        );
        clearInterval(intervalId);
      }
    };
  }, [isOwner, gameExists, fullGameData?.id, fetchPlayers]); // Dependencies for starting/stopping polling

  // --- NEW Effect for Polling Game State (Owner Only) ---
  useEffect(() => {
    let gameStateIntervalId = null;

    // Poll only if owner and game exists
    if (isOwner && gameExists && fullGameData?.id) {
      console.log(
        `Home: Starting game state polling for game ${fullGameData.id}`
      );

      gameStateIntervalId = setInterval(async () => {
        console.log(`Home: Polling game state for game ${fullGameData.id}`);
        const updatedGameData = await fetchFullGameData(fullGameData.id);
        if (updatedGameData) {
          // Update state only if changed? Or always?
          // For simplicity, always update. Could compare JSON strings for optimization.
          setFullGameData(updatedGameData);
        }
      }, 5000); // Poll every 5 seconds (adjust interval as needed)
    }

    // Cleanup function
    return () => {
      if (gameStateIntervalId) {
        console.log(
          `Home: Stopping game state polling for game ${fullGameData?.id}`
        );
        clearInterval(gameStateIntervalId);
      }
    };
  }, [isOwner, gameExists, fullGameData?.id, fetchFullGameData]); // Dependencies for state polling

  const handleGameCreated = (creationResponse) => {
    console.log("Game created successfully, updating display.");
    if (creationResponse.game) {
      // Assuming creation response includes full initial state (LOBBY, R0, S0)
      setFullGameData(creationResponse.game);
      setGameExists(true);
      setIsOwner(true); // Creator is always owner
      setPlayersList([]);
    } else {
      console.error(
        "Game created, but failed to receive full game data in response."
      );
      setError(
        "Game created, but couldn't load details immediately. Please refresh."
      );
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const handleDisconnectPage = async () => {
    try {
      setLoading(true);
      const token = await auth.currentUser?.getIdToken();

      const response = await fetch(
        `${getApiBaseUrl()}/api/pages/disconnect-page`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        // After disconnecting, sign out the user
        await signOut(auth);
        navigate("/"); // Redirect to login page
      } else {
        // Handle disconnect error
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || "Failed to disconnect page.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error disconnecting page:", error);
      setError("An error occurred while disconnecting the page.");
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading game page...</div>;
  }

  if (error) {
    return <div className="error-message">Error: {error}</div>;
  }

  return (
    <div className="home-container">
      {/* Case 1: Owner, Game Not Created Yet -> Show Create Form */}
      {isOwner && !gameExists && (
        <>
          <GameCreationForm
            pagePath={pagePath}
            onGameCreated={handleGameCreated}
          />
          {auth.currentUser && (
            <p style={{ fontSize: "0.9em", color: "#555" }}>
              Logged in as: {auth.currentUser.email}
            </p>
          )}
          <p>Creator</p>
        </>
      )}

      {/* Case 2: Owner, Game Exists -> Show Edit Form */}
      {isOwner && gameExists && fullGameData && (
        <>
          <GameCreationForm
            gameId={fullGameData.id}
            existingGameData={fullGameData}
            playersList={playersList}
            playersLoading={playersLoading}
            playersError={playersError}
            fetchPlayers={fetchPlayers}
            setFullGameData={setFullGameData}
          />
          {auth.currentUser && (
            <p style={{ fontSize: "0.9em", color: "#555" }}>
              Logged in as: {auth.currentUser.email}
            </p>
          )}
          <p>Creator</p>
        </>
      )}

      {/* Case 3: Not Owner, Game Exists -> Show Display Only */}
      {!isOwner && gameExists && fullGameData && (
        <>
          <GameDisplay game={fullGameData} />
          {auth.currentUser && (
            <p style={{ fontSize: "0.9em", color: "#555" }}>
              Logged in as: {auth.currentUser.email}
            </p>
          )}
          <p>Viewer</p>
        </>
      )}

      {/* Loading/Not Found States - These might need adjustment if headers are moved */}
      {/* Example: Owner, but game details haven't loaded yet for edit view */}
      {isOwner && gameExists && !fullGameData && <p>Loading game details...</p>}
      {/* Example: Not Owner, game exists, but details haven't loaded */}
      {!isOwner && gameExists && !fullGameData && (
        <p>Loading game details...</p>
      )}

      {/* This covers the case where the viewer is not owner AND game isn't created */}
      {!isOwner && !gameExists && (
        <>
          {auth.currentUser && (
            <p style={{ fontSize: "0.9em", color: "#555" }}>
              Logged in as: {auth.currentUser.email}
            </p>
          )}
          <p>Viewer</p>
        </>
      )}

      {/* Actions (Disconnect Button) - Stays at the bottom */}
      {isOwner /* Only show disconnect if owner */ && (
        <div className="actions">
          <button onClick={handleDisconnectPage} className="disconnect-btn">
            End Game and Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

export default Home;
